import { useEffect, useRef, useCallback } from 'react'

/**
 * @author Jorge Perales
 * @updatedDate 03-06-26
 * @description Hook to track conversation visibility and emit read-state events through sockets
 * @param {Object} params
 * @param {string} params.conversationId - Active conversation ID
 * @param {Object} params.socketRef - Ref del socket.io-client
 * @param {'end_user'|'support_agent'} params.viewedBy - Viewer key (must match history.author.key)
 * @param {boolean} params.enabled - Flag to enable or disable tracking
 * @returns {Object} { isViewing: boolean }
 */
const useConversationVisibility = ({
  conversationId,
  socketRef,
  viewedBy,
  enabled = true
}) => {
  const isViewingRef = useRef(false)
  const debounceRef = useRef(null)
  const lastConversationRef = useRef(null)

  // Calculate whether the user is actually viewing the conversation.
  const calculateVisibility = useCallback(() => {
    return (
      document.visibilityState === 'visible' &&
      !!conversationId
    )
  }, [conversationId])

  // Emit visibility changes with debounce to avoid spam.
  const emitViewChange = useCallback((isViewing) => {
    if (!enabled || !conversationId || !socketRef?.current?.connected) return

    const wasViewing = isViewingRef.current === true
    const isNowViewing = isViewing === true
    const sameConversation = lastConversationRef.current === conversationId

    const shouldEmit = !(wasViewing && isNowViewing && sameConversation)
    
    if (!shouldEmit) return

    isViewingRef.current = isViewing
    lastConversationRef.current = conversationId

    // Clear any previous debounce timer.
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      const eventName = viewedBy === 'end_user' ? 'endUserViewChange' : 'conversationViewChange'

      socketRef.current.emit(eventName, {
        conversationId,
        viewedBy,
        isViewing,
        timestamp: new Date().toISOString()
      })
    }, 300) // 300ms debounce
  }, [conversationId, viewedBy, enabled, socketRef])

  useEffect(() => {
    if (!enabled || !conversationId || !socketRef?.current) return

    // Centralized handler to re-evaluate visibility.
    const handleVisibilityUpdate = () => {
      const isVisible = calculateVisibility()
      emitViewChange(isVisible)
    }

    // Native listeners.
    const onVisibilityChange = () => handleVisibilityUpdate()
    const onFocus = () => handleVisibilityUpdate()

    // Register listeners.
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)

    // Initial evaluation.
    handleVisibilityUpdate()

    // Cleanup.
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      
      // Emit that we stopped viewing on unmount or conversation change.
      if (isViewingRef.current) {
        emitViewChange(false)
      }
      
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onFocus)
    }
  }, [conversationId, enabled, socketRef, calculateVisibility, emitViewChange])

  // Reset the tracker when the conversation changes.
  useEffect(() => {
    if (lastConversationRef.current && lastConversationRef.current !== conversationId) {
      
      // 1. Emit that we stopped viewing the previous conversation.
      if (isViewingRef.current) {
        emitViewChange(false)
      }
      
      // 2. Reset internal state.
      isViewingRef.current = false
      lastConversationRef.current = conversationId
      
      // 3. Force a re-evaluation for the new conversation.
      const timer = setTimeout(() => {
        if (enabled && conversationId && socketRef.current?.connected) {
          const isVisible = calculateVisibility()
          if (isVisible) {
            emitViewChange(true)
          }
        }
      }, 100)
      
      // Cleanup the timer if the effect is cleaned up earlier.
      return () => clearTimeout(timer)
    }
    
    // If a conversation is assigned for the first time.
    if (!lastConversationRef.current && conversationId) {
      lastConversationRef.current = conversationId
    }
  }, [conversationId, emitViewChange, calculateVisibility, enabled, socketRef])

  return {
    isViewing: isViewingRef.current
  }
}

export default useConversationVisibility